import React from "react";
import PropTypes from "prop-types";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { PiUserCircle } from "react-icons/pi";
import { Button } from "@/components/buttons";
import borrowerInfoValidation from "@/modules/client/loan-application/features/loan-origination/validations/borrower-info-validation";
import CoBorrowerFields from "@/modules/client/loan-application/features/loan-origination/sub-features/additional-details/components/co-borrower-fields";

const DEFAULTS = {
  firstName: "", lastName: "", email: "",
  phoneNumber: "", mobileNumber: "",
  ficoScore: 0, liquidity: 0, netWorth: 0, ownershipPercentage: 0,
  isUsCitizen: true, hasBeenBankruptcy: false,
};

/**
 * CoBorrowerModal — add / edit co-borrower form opened via the overlay system.
 *
 * Props are received directly (not from Redux):
 *   handleClose — injected by OverlayModal
 *   onSave      — callback to append/update the co-borrower in the parent field array
 *   defaultValues — pre-filled values when editing
 */
export default function CoBorrowerModal({ handleClose, onSave, defaultValues }) {
  const { control, handleSubmit, formState: { errors } } = useForm({
    resolver: yupResolver(borrowerInfoValidation),
    defaultValues: { ...DEFAULTS, ...defaultValues },
  });

  const onSubmit = data => { onSave(data); handleClose(); };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="p-6">
      <div className="mb-5 flex flex-col items-center">
        <div className="flex size-12 items-center justify-center rounded-full bg-green-100">
          <PiUserCircle className="size-6 text-green-600" aria-hidden="true" />
        </div>
        <h3 className="mt-3 text-lg font-semibold text-gray-900 dark:text-white">
          Co-Borrower Information
        </h3>
        <p className="text-sm text-gray-500 dark:text-slate-400">
          Provide the co-borrower&apos;s details.
        </p>
      </div>
      <CoBorrowerFields control={control} errors={errors} />
      <div className="mt-6 flex justify-end gap-3">
        <Button type="button" variant="outline" label="Cancel" onClick={handleClose} />
        <Button type="submit" variant="primary" label="Save" />
      </div>
    </form>
  );
}

CoBorrowerModal.propTypes = {
  handleClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  defaultValues: PropTypes.object,
};
